import React from 'react'
import EditableText from '@/components/editable/EditableText'
import EditableImage from '@/components/editable/EditableImage'

const FEATURES = [
  { t: 'Mobile', d: "Déployable sur sites partenaires, événements et terrains d'expérimentation." },
  { t: 'Intelligent', d: "Équipé d'outils IA, studio de captation et postes de création modernes." },
  { t: 'Connecté', d: "Plateformes en ligne, contenus pédagogiques, diffusion en direct." },
  { t: 'Identifiable', d: "Une signature visuelle reconnaissable, un univers immersif cohérent." },
]

const APPLICATIONS = [
  'Formations mobiles',
  'Ateliers IA',
  'Studio de captation',
  'Laboratoire créatif',
  'Espace événementiel',
  'Interventions institutionnelles',
  'Dispositifs jeunesse',
  'Événements culturels',
  'Expérimentations pédagogiques',
]

const Conteneur: React.FC = () => {
  return (
    <section className="relative bg-[#0E1B2C] py-32 lg:py-44 overflow-hidden">
      <div className="max-w-[1440px] mx-auto px-6 lg:px-12">
        <div className="grid lg:grid-cols-12 gap-12 lg:gap-20 items-center">
          {/* Visual */}
          <div className="lg:col-span-7 reveal order-2 lg:order-1">
            <div className="relative aspect-[4/3] hairline-strong overflow-hidden bg-[#0E1B2C]">
              <EditableImage
                contentKey="conteneur.img"
                src="https://static.lumi.new/1e/1e9d2f8073e23dd41b071429aede6f6b.png"
                alt="Conteneur pilote du Centre de Transformation"
                section="Conteneur"
                label="Image du conteneur"
                className="absolute inset-0 w-full h-full object-cover opacity-90"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-[#0E1B2C] via-[#0E1B2C]/30 to-transparent" />

              {/* Annotations */}
              <div className="absolute top-6 left-6 right-6 flex justify-between items-start">
                <div>
                  <div className="text-[10px] tracking-luxe text-[#C9E26A]"><EditableText contentKey="conteneur.overlay.label1" section="Conteneur" label="Étiquette — Conteneur pilote" as="span">CONTENEUR PILOTE</EditableText></div>
                  <div className="font-serif-display text-[22px] text-[#E8DCC4] mt-2"><EditableText contentKey="conteneur.overlay.module" section="Conteneur" label="Module" as="span">Module 01</EditableText></div>
                </div>
                <div className="text-right">
                  <div className="text-[10px] tracking-luxe text-[#C8A24A]"><EditableText contentKey="conteneur.overlay.label2" section="Conteneur" label="Étiquette — Un espace" as="span">UN ESPACE</EditableText></div>
                  <div className="text-[12px] text-[#E8DCC4] mt-1"><EditableText contentKey="conteneur.overlay.desc1" section="Conteneur" label="Description — Mobile Intelligent Connecté" as="span">Mobile · Intelligent · Connecté</EditableText></div>
                </div>
              </div>

              <div className="absolute bottom-6 left-6 right-6 flex flex-wrap items-end justify-between gap-4">
                <div className="space-y-1">
                  <div className="text-[10px] tracking-luxe text-[#C9BFA8]/70"><EditableText contentKey="conteneur.overlay.label3" section="Conteneur" label="Étiquette — Caractère" as="span">CARACTÈRE</EditableText></div>
                  <div className="font-serif-display text-[28px] italic text-[#C9E26A]"><EditableText contentKey="conteneur.overlay.desc2" section="Conteneur" label="Description — Mobile Intelligent" as="span">Mobile · Intelligent</EditableText></div>
                </div>
                <div className="flex items-center gap-2 text-[11px] tracking-mid uppercase text-[#C9BFA8]">
                  <span className="inline-block w-3 h-2 bg-[#D52B1E]" aria-hidden />
                  <EditableText contentKey="conteneur.overlay.swiss" section="Conteneur" label="Étiquette — Conçu en Suisse" as="span">Conçu en Suisse</EditableText>
                </div>
              </div>
            </div>
          </div>

          {/* Text */}
          <div className="lg:col-span-5 reveal order-1 lg:order-2">
            <div className="text-[10px] tracking-luxe text-[#C8A24A] mb-4">
              <EditableText as="span" contentKey="conteneur.section.kicker" section="Conteneur" label="Numéro de section">05 — DISPOSITIF</EditableText>
            </div>
            <div className="divider-gold w-24 mb-8" />
            <h3 className="font-serif-display text-[36px] sm:text-[44px] lg:text-[56px] leading-[1.05] text-[#E8DCC4] mb-8 text-balance">
              <EditableText contentKey="conteneur.title" section="Conteneur" label="Titre" multiline as="span">
                Un conteneur pilote pour ancrer l'expérience.
              </EditableText>
            </h3>
            <p className="text-[16px] leading-[1.75] text-[#C9BFA8] mb-6">
              <EditableText contentKey="conteneur.body1" section="Conteneur" label="Paragraphe 1" multiline as="span">
                Le conteneur pilote constitue le premier module physique du Centre de Transformation. Pensé comme un espace mobile, autonome et évolutif, il permet d'amener les outils, les expériences et les contenus directement sur le terrain.
              </EditableText>
            </p>
            <p className="text-[15px] leading-[1.75] text-[#C9BFA8]/80 mb-10">
              <EditableText contentKey="conteneur.body2" section="Conteneur" label="Paragraphe 2" multiline as="span">
                Premier prototype tangible du Centre, il sert de laboratoire pour tester nos formats, capter nos contenus et créer un repère visuel fort.
              </EditableText>
            </p>

            <ul className="space-y-5">
              {FEATURES.map((f, i) => (
                <li key={f.t} className="flex gap-5 items-start" style={{ transitionDelay: `${i * 60}ms` }}>
                  <div className="mt-2 w-8 h-px bg-[#C9E26A] shrink-0" />
                  <div>
                    <div className="text-[14px] tracking-mid uppercase text-[#E8DCC4] mb-1">
                      <EditableText contentKey={`conteneur.feature.${i}.title`} section="Conteneur" label={`Feature ${i + 1} - Titre`} as="span">
                        {f.t}
                      </EditableText>
                    </div>
                    <div className="text-[14px] leading-[1.6] text-[#C9BFA8]">
                      <EditableText contentKey={`conteneur.feature.${i}.desc`} section="Conteneur" label={`Feature ${i + 1} - Description`} multiline as="span">
                        {f.d}
                      </EditableText>
                    </div>
                  </div>
                </li>
              ))}
            </ul>

            {/* Applications possibles */}
            <div className="mt-12 pt-12 border-t border-[rgba(232,220,196,0.10)]">
              <div className="text-[10px] tracking-luxe text-[#C8A24A] mb-5">
                <EditableText as="span" contentKey="conteneur.applications.kicker" section="Conteneur" label="Titre — Applications possibles">APPLICATIONS POSSIBLES</EditableText>
              </div>
              <div className="grid grid-cols-2 gap-x-4 gap-y-3">
                {APPLICATIONS.map((a, ai) => (
                  <div key={a} className="flex items-center gap-3 text-[13px] text-[#C9BFA8]">
                    <span className="w-1 h-1 rounded-full bg-[#C9E26A] shrink-0" />
                    <EditableText contentKey={`conteneur.applications.${ai}`} section="Conteneur" label={`Application ${ai + 1}`} as="span">{a}</EditableText>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>

        {/* Une structure reproductible — full-width band */}
        <div className="mt-24 lg:mt-32 pt-16 border-t border-[rgba(232,220,196,0.10)] text-center max-w-3xl mx-auto reveal">
          <div className="text-[10px] tracking-luxe text-[#C8A24A] mb-4">
            <EditableText as="span" contentKey="conteneur.closing.kicker" section="Conteneur" label="Titre — Structure reproductible">UNE STRUCTURE REPRODUCTIBLE</EditableText>
          </div>
          <div className="divider-gold w-24 mx-auto mb-10" />
          <p className="font-serif-display text-[24px] lg:text-[32px] leading-[1.35] text-[#E8DCC4] italic text-balance">
            <EditableText contentKey="conteneur.closing" section="Conteneur" label="Phrase de cloture" multiline as="span">
              Le prototype pilote a vocation à devenir un modèle reproductible pouvant être déployé dans différents contextes et territoires.
            </EditableText>
          </p>
        </div>
      </div>
    </section>
  )
}

export default Conteneur
